/**
 * Risk Mapping Tab - טאב מיפוי סיכון
 * Manage risk score mappings
 */

import { useEffect, useState } from 'react'
import { useAdminStore } from '../../store/adminStore'
import { Plus, Edit2, Check, X, Eye, RefreshCw } from 'lucide-react'
import type { RiskScoreMapping } from '../../types'
import SystemModal from '../common/SystemModal'

export default function RiskMappingTab() {
  const {
    riskMappings,
    riskMappingsLoading,
    riskMappingsError,
    fetchRiskMappings,
    clearRiskMappingsError
  } = useAdminStore()

  const [includeArchived, setIncludeArchived] = useState(false)
  const [viewMapping, setViewMapping] = useState<RiskScoreMapping | null>(null)
  const [showViewModal, setShowViewModal] = useState(false)
  const [isEditMode, setIsEditMode] = useState(false)
  const [editForm, setEditForm] = useState<any>({})
  const [saving, setSaving] = useState(false)

  // System Modal state
  const [systemModal, setSystemModal] = useState<{
    isOpen: boolean
    type: 'success' | 'error' | 'warning' | 'info'
    title?: string
    message: string
  }>({
    isOpen: false,
    type: 'info',
    message: ''
  })

  const handleViewMapping = (mapping: RiskScoreMapping) => {
    setViewMapping(mapping)
    setEditForm({
      minScore: mapping.minScore,
      maxScore: mapping.maxScore,
      description: mapping.description,
      stocks: mapping.assetAllocation.stocks,
      bonds: mapping.assetAllocation.bonds,
      cash: mapping.assetAllocation.cash,
      alternatives: mapping.assetAllocation.alternatives,
      minimumInvestment: mapping.minimumInvestment,
      recommendedTimeHorizon: mapping.recommendedTimeHorizon,
      changeReason: ''
    })
    setIsEditMode(false)
    setShowViewModal(true)
  }

  const handleEditMapping = (mapping: RiskScoreMapping) => {
    setViewMapping(mapping)
    setEditForm({
      minScore: mapping.minScore,
      maxScore: mapping.maxScore,
      description: mapping.description,
      stocks: mapping.assetAllocation.stocks,
      bonds: mapping.assetAllocation.bonds,
      cash: mapping.assetAllocation.cash,
      alternatives: mapping.assetAllocation.alternatives,
      minimumInvestment: mapping.minimumInvestment,
      recommendedTimeHorizon: mapping.recommendedTimeHorizon,
      changeReason: ''
    })
    setIsEditMode(true)
    setShowViewModal(true)
  }

  const handleCloseModal = () => {
    setShowViewModal(false)
    setViewMapping(null)
    setIsEditMode(false)
    setEditForm({})
  }

  const handleAllocationChange = (field: 'stocks' | 'bonds' | 'cash' | 'alternatives', value: number) => {
    // Calculate what the total would be with the new value
    const currentTotal =
      (field === 'stocks' ? value : editForm.stocks) +
      (field === 'bonds' ? value : editForm.bonds) +
      (field === 'cash' ? value : editForm.cash) +
      (field === 'alternatives' ? value : editForm.alternatives)

    // Don't allow if it would exceed 100%
    if (currentTotal > 100) {
      setSystemModal({
        isOpen: true,
        type: 'warning',
        title: 'חריגה מ-100%',
        message: `לא ניתן להזין ${value}% - הסכום הכולל יעבור 100%\n\nסכום נוכחי: ${currentTotal}%\nמקסימום אפשרי: ${100 - (currentTotal - value)}%`
      })
      return
    }

    // Update the form
    setEditForm({ ...editForm, [field]: value })
  }

  const getTotalAllocation = () => {
    return editForm.stocks + editForm.bonds + editForm.cash + editForm.alternatives
  }

  const handleResetAllocation = () => {
    setEditForm({
      ...editForm,
      stocks: 25,
      bonds: 25,
      cash: 25,
      alternatives: 25
    })
  }

  const handleSaveEdit = async () => {
    if (!viewMapping) return

    // Strict validation: must be exactly 100%
    const total = getTotalAllocation()
    if (total !== 100) {
      setSystemModal({
        isOpen: true,
        type: 'error',
        title: 'סכום לא תקין',
        message: `הקצאת הנכסים חייבת להיות בדיוק 100%\n\nסכום נוכחי: ${total}%\n${total < 100 ? `חסר: ${100 - total}%` : `עודף: ${total - 100}%`}`
      })
      return
    }

    if (!editForm.changeReason || editForm.changeReason.length < 10) {
      setSystemModal({
        isOpen: true,
        type: 'warning',
        title: 'סיבת שינוי חסרה',
        message: 'נא להזין סיבת שינוי מפורטת (לפחות 10 תווים)'
      })
      return
    }

    setSaving(true)
    try {
      // This will be implemented when we add the updateRiskMapping to the store
      setSystemModal({
        isOpen: true,
        type: 'info',
        title: 'בקרוב',
        message: 'שמירת שינויים - פונקציונליות תתווסף בקרוב'
      })
      // await updateRiskMapping(viewMapping._id, {
      //   minScore: editForm.minScore,
      //   maxScore: editForm.maxScore,
      //   description: editForm.description,
      //   assetAllocation: {
      //     stocks: editForm.stocks,
      //     bonds: editForm.bonds,
      //     cash: editForm.cash,
      //     alternatives: editForm.alternatives
      //   },
      //   minimumInvestment: editForm.minimumInvestment,
      //   recommendedTimeHorizon: editForm.recommendedTimeHorizon,
      //   changeReason: editForm.changeReason
      // })
      // handleCloseModal()
      // await fetchRiskMappings(includeArchived)
    } catch (error) {
      console.error('Error saving:', error)
      setSystemModal({
        isOpen: true,
        type: 'error',
        title: 'שגיאה',
        message: 'אירעה שגיאה בשמירת השינויים. אנא נסה שוב.'
      })
    } finally {
      setSaving(false)
    }
  }

  useEffect(() => {
    fetchRiskMappings(includeArchived)
  }, [includeArchived])

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'active':
        return <span className="badge badge-success">פעיל</span>
      case 'draft':
        return <span className="badge badge-gray">טיוטה</span>
      case 'pending_approval':
        return <span className="badge badge-warning">ממתין לאישור</span>
      case 'archived':
        return <span className="badge badge-gray">בארכיון</span>
      default:
        return <span className="badge">{status}</span>
    }
  }

  const getAllocationColor = (value: number) => {
    if (value >= 70) return 'text-green-600 font-bold'
    if (value >= 40) return 'text-blue-600 font-semibold'
    if (value >= 20) return 'text-yellow-600'
    return 'text-gray-600'
  }

  return (
    <div className="space-y-6">
      {/* Header with Actions */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-heading font-bold">מיפוי סיכון</h2>
          <p className="text-gray-600 mt-1">
            ניהול טווחי ציוני סיכון והקצאות נכסים
          </p>
        </div>

        <div className="flex gap-2">
          <button
            onClick={() => fetchRiskMappings(includeArchived)}
            disabled={riskMappingsLoading}
            className="btn btn-outline"
          >
            <RefreshCw
              size={18}
              className={riskMappingsLoading ? 'animate-spin' : ''}
              aria-hidden="true"
            />
            רענון
          </button>
          <button className="btn btn-primary">
            <Plus size={18} aria-hidden="true" />
            מיפוי חדש
          </button>
        </div>
      </div>

      {/* Filter */}
      <div className="flex items-center gap-4">
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={includeArchived}
            onChange={(e) => setIncludeArchived(e.target.checked)}
            className="rounded text-primary-600"
          />
          <span className="text-sm">הצג גם מיפויים בארכיון</span>
        </label>
      </div>

      {/* Error Alert */}
      {riskMappingsError && (
        <div className="alert alert-error" role="alert">
          <p>{riskMappingsError}</p>
          <button onClick={clearRiskMappingsError} className="btn btn-sm btn-outline">
            סגור
          </button>
        </div>
      )}

      {/* Mappings Grid */}
      {riskMappingsLoading ? (
        <div className="text-center py-12 text-gray-500">טוען מיפויים...</div>
      ) : riskMappings.length === 0 ? (
        <div className="text-center py-12 text-gray-500">
          <p>לא נמצאו מיפויי סיכון</p>
          <button className="btn btn-primary mt-4">
            <Plus size={18} aria-hidden="true" />
            צור מיפוי ראשון
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {riskMappings.map((mapping) => (
            <div
              key={mapping._id}
              className={`card hover:shadow-lg transition-shadow ${
                mapping.status === 'active'
                  ? 'border-2 border-primary-200'
                  : ''
              }`}
            >
              {/* Header */}
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-gray-900">
                    {mapping.profileNameHebrew}
                  </h3>
                  <p className="text-sm text-gray-600">{mapping.profileName}</p>
                </div>
                {getStatusBadge(mapping.status)}
              </div>

              {/* Score Range */}
              <div className="bg-primary-50 rounded-lg p-3 mb-4">
                <p className="text-sm text-gray-600 mb-1">טווח ציון</p>
                <p className="text-2xl font-bold text-primary-600">
                  {mapping.minScore.toFixed(1)} - {mapping.maxScore.toFixed(1)}
                </p>
              </div>

              {/* Asset Allocation */}
              <div className="mb-4">
                <p className="text-sm font-medium text-gray-700 mb-2">
                  הקצאת נכסים
                </p>
                <div className="space-y-1 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">מניות</span>
                    <span className={getAllocationColor(mapping.assetAllocation.stocks)}>
                      {mapping.assetAllocation.stocks}%
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">אג״ח</span>
                    <span className={getAllocationColor(mapping.assetAllocation.bonds)}>
                      {mapping.assetAllocation.bonds}%
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">מזומן</span>
                    <span className={getAllocationColor(mapping.assetAllocation.cash)}>
                      {mapping.assetAllocation.cash}%
                    </span>
                  </div>
                  {mapping.assetAllocation.alternatives > 0 && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">אלטרנטיבי</span>
                      <span
                        className={getAllocationColor(
                          mapping.assetAllocation.alternatives
                        )}
                      >
                        {mapping.assetAllocation.alternatives}%
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {/* Investment Params */}
              <div className="border-t pt-4 mb-4 space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">סכום מינימום</span>
                  <span className="font-medium">
                    ₪{mapping.minimumInvestment.toLocaleString()}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">אופק זמן</span>
                  <span className="font-medium">
                    {mapping.recommendedTimeHorizon}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">גרסה</span>
                  <span className="font-medium">v{mapping.version}</span>
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-2">
                <button
                  onClick={() => handleViewMapping(mapping)}
                  className="btn btn-sm btn-outline flex-1"
                  title="צפייה"
                  aria-label="צפייה"
                >
                  <Eye size={16} aria-hidden="true" />
                </button>
                {mapping.status !== 'archived' && (
                  <>
                    <button
                      onClick={() => handleEditMapping(mapping)}
                      className="btn btn-sm btn-outline flex-1"
                      title="עריכה"
                      aria-label="עריכה"
                    >
                      <Edit2 size={16} aria-hidden="true" />
                    </button>
                    {mapping.status === 'pending_approval' && (
                      <button
                        className="btn btn-sm btn-primary flex-1"
                        title="אישור"
                        aria-label="אישור"
                      >
                        <Check size={16} aria-hidden="true" />
                      </button>
                    )}
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Info Box */}
      <div className="alert alert-info">
        <p className="text-sm">
          💡 <strong>טיפ:</strong> מיפויי סיכון פעילים משפיעים על כל המשתמשים
          במערכת. שינויים נשמרים בגרסאות לשמירת תאימות.
        </p>
      </div>

      {/* View/Edit Modal */}
      {showViewModal && viewMapping && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between">
              <div>
                <h2 className="text-xl font-bold text-gray-900">
                  {viewMapping.profileNameHebrew}
                </h2>
                <p className="text-sm text-gray-600">{viewMapping.profileName}</p>
              </div>
              <div className="flex items-center gap-2">
                {getStatusBadge(viewMapping.status)}
                <button
                  onClick={handleCloseModal}
                  className="btn btn-sm btn-outline"
                  aria-label="סגירה"
                >
                  <X size={18} aria-hidden="true" />
                </button>
              </div>
            </div>

            {/* Modal Content */}
            <div className="p-6 space-y-6">
              {/* Score Range */}
              <div className="card bg-primary-50">
                <h3 className="font-semibold text-gray-900 mb-3">טווח ציון סיכון</h3>
                <div className="flex items-center gap-4">
                  <div>
                    <p className="text-sm text-gray-600">מינימום</p>
                    <p className="text-3xl font-bold text-primary-600">
                      {viewMapping.minScore.toFixed(1)}
                    </p>
                  </div>
                  <div className="text-2xl text-gray-400">-</div>
                  <div>
                    <p className="text-sm text-gray-600">מקסימום</p>
                    <p className="text-3xl font-bold text-primary-600">
                      {viewMapping.maxScore.toFixed(1)}
                    </p>
                  </div>
                </div>
              </div>

              {/* Description */}
              <div className="card">
                <h3 className="font-semibold text-gray-900 mb-2">תיאור</h3>
                {isEditMode ? (
                  <textarea
                    value={editForm.description}
                    onChange={(e) =>
                      setEditForm({ ...editForm, description: e.target.value })
                    }
                    aria-label="תיאור הפרופיל"
                    className="w-full border rounded-lg p-3 text-sm min-h-[100px]"
                    placeholder="תיאור הפרופיל..."
                  />
                ) : (
                  <p className="text-gray-700 leading-relaxed">{viewMapping.description}</p>
                )}
              </div>

              {/* Asset Allocation */}
              <div className="card">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-semibold text-gray-900">
                    הקצאת נכסים
                    {isEditMode && (
                      <span
                        className={`text-sm font-bold mr-2 ${
                          getTotalAllocation() === 100
                            ? 'text-green-600'
                            : getTotalAllocation() > 100
                            ? 'text-red-600'
                            : 'text-yellow-600'
                        }`}
                      >
                        (סה״כ: {getTotalAllocation()}%
                        {getTotalAllocation() === 100 ? ' ✓' : getTotalAllocation() > 100 ? ' ✗ חריגה!' : ' - חסר'}
                        )
                      </span>
                    )}
                  </h3>
                  {isEditMode && (
                    <button
                      onClick={handleResetAllocation}
                      className="btn btn-sm btn-outline text-xs"
                      title="איפוס ל-25% לכל קטגוריה"
                    >
                      איפוס ל-25% לכל
                    </button>
                  )}
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="text-center">
                    <p className="text-sm text-gray-600 mb-1">מניות</p>
                    {isEditMode ? (
                      <input
                        type="number"
                        dir="ltr"
                        aria-label="הקצאת מניות באחוזים"
                        value={editForm.stocks}
                        onChange={(e) =>
                          handleAllocationChange('stocks', parseFloat(e.target.value) || 0)
                        }
                        className="w-full text-center text-2xl font-bold border-2 border-blue-300 rounded p-2 focus:border-blue-500"
                        min="0"
                        max="100"
                        step="5"
                      />
                    ) : (
                      <p className={`text-2xl font-bold ${getAllocationColor(viewMapping.assetAllocation.stocks)}`}>
                        {viewMapping.assetAllocation.stocks}%
                      </p>
                    )}
                  </div>
                  <div className="text-center">
                    <p className="text-sm text-gray-600 mb-1">אג״ח</p>
                    {isEditMode ? (
                      <input
                        type="number"
                        dir="ltr"
                        aria-label="הקצאת אג״ח באחוזים"
                        value={editForm.bonds}
                        onChange={(e) =>
                          handleAllocationChange('bonds', parseFloat(e.target.value) || 0)
                        }
                        className="w-full text-center text-2xl font-bold border-2 border-blue-300 rounded p-2 focus:border-blue-500"
                        min="0"
                        max="100"
                        step="5"
                      />
                    ) : (
                      <p className={`text-2xl font-bold ${getAllocationColor(viewMapping.assetAllocation.bonds)}`}>
                        {viewMapping.assetAllocation.bonds}%
                      </p>
                    )}
                  </div>
                  <div className="text-center">
                    <p className="text-sm text-gray-600 mb-1">מזומן</p>
                    {isEditMode ? (
                      <input
                        type="number"
                        dir="ltr"
                        aria-label="הקצאת מזומן באחוזים"
                        value={editForm.cash}
                        onChange={(e) =>
                          handleAllocationChange('cash', parseFloat(e.target.value) || 0)
                        }
                        className="w-full text-center text-2xl font-bold border-2 border-blue-300 rounded p-2 focus:border-blue-500"
                        min="0"
                        max="100"
                        step="5"
                      />
                    ) : (
                      <p className={`text-2xl font-bold ${getAllocationColor(viewMapping.assetAllocation.cash)}`}>
                        {viewMapping.assetAllocation.cash}%
                      </p>
                    )}
                  </div>
                  <div className="text-center">
                    <p className="text-sm text-gray-600 mb-1">אלטרנטיבי</p>
                    {isEditMode ? (
                      <input
                        type="number"
                        dir="ltr"
                        aria-label="הקצאת נכסים אלטרנטיביים באחוזים"
                        value={editForm.alternatives}
                        onChange={(e) =>
                          handleAllocationChange('alternatives', parseFloat(e.target.value) || 0)
                        }
                        className="w-full text-center text-2xl font-bold border-2 border-blue-300 rounded p-2 focus:border-blue-500"
                        min="0"
                        max="100"
                        step="5"
                      />
                    ) : (
                      <p className={`text-2xl font-bold ${getAllocationColor(viewMapping.assetAllocation.alternatives)}`}>
                        {viewMapping.assetAllocation.alternatives}%
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* Investment Parameters */}
              <div className="card">
                <h3 className="font-semibold text-gray-900 mb-3">פרמטרי השקעה</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-600 mb-1">סכום מינימום</p>
                    {isEditMode ? (
                      <input
                        type="number"
                        dir="ltr"
                        aria-label="סכום השקעה מינימלי בשקלים"
                        value={editForm.minimumInvestment}
                        onChange={(e) =>
                          setEditForm({
                            ...editForm,
                            minimumInvestment: parseFloat(e.target.value) || 0
                          })
                        }
                        className="w-full text-lg font-semibold border rounded p-2"
                        min="0"
                        step="1000"
                      />
                    ) : (
                      <p className="text-lg font-semibold">
                        ₪{viewMapping.minimumInvestment.toLocaleString()}
                      </p>
                    )}
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 mb-1">אופק זמן מומלץ</p>
                    {isEditMode ? (
                      <input
                        type="text"
                        aria-label="אופק זמן מומלץ"
                        value={editForm.recommendedTimeHorizon}
                        onChange={(e) =>
                          setEditForm({
                            ...editForm,
                            recommendedTimeHorizon: e.target.value
                          })
                        }
                        className="w-full text-lg font-semibold border rounded p-2"
                        placeholder="למשל: 5-7 שנים"
                      />
                    ) : (
                      <p className="text-lg font-semibold">
                        {viewMapping.recommendedTimeHorizon}
                      </p>
                    )}
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">רמת פרופיל</p>
                    <p className="text-lg font-semibold">{viewMapping.profileLevel}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">גרסה</p>
                    <p className="text-lg font-semibold">v{viewMapping.version}</p>
                  </div>
                </div>
              </div>

              {/* Change Reason (Edit Mode Only) */}
              {isEditMode && (
                <div className="card bg-yellow-50 border-2 border-yellow-400">
                  <h3 className="font-semibold text-gray-900 mb-2">
                    סיבת שינוי <span className="text-red-600">*</span>
                  </h3>
                  <p className="text-sm text-gray-600 mb-2">
                    נדרשת סיבה מפורטת לשינוי מיפוי סיכון (לפחות 10 תווים)
                  </p>
                  <textarea
                    value={editForm.changeReason}
                    onChange={(e) =>
                      setEditForm({ ...editForm, changeReason: e.target.value })
                    }
                    aria-label="סיבת שינוי"
                    className="w-full border rounded-lg p-3 text-sm min-h-[80px]"
                    placeholder="למשל: עדכון הקצאת נכסים בהתאם לשינוי בתנאי שוק..."
                  />
                </div>
              )}

              {/* Regulatory Disclosures */}
              {viewMapping.regulatoryDisclosures && viewMapping.regulatoryDisclosures.length > 0 && (
                <div className="card">
                  <h3 className="font-semibold text-gray-900 mb-3">
                    גילויים רגולטוריים ({viewMapping.regulatoryDisclosures.length})
                  </h3>
                  <div className="space-y-3">
                    {viewMapping.regulatoryDisclosures.map((disclosure, index) => (
                      <div
                        key={index}
                        className="border-r-4 border-yellow-400 bg-yellow-50 p-3 rounded"
                      >
                        <p className="font-semibold text-sm text-gray-900 mb-1">
                          {disclosure.title}
                          {disclosure.required && (
                            <span className="text-red-600 mr-1">*</span>
                          )}
                        </p>
                        <p className="text-sm text-gray-700">{disclosure.content}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Metadata */}
              <div className="card bg-gray-50">
                <h3 className="font-semibold text-gray-900 mb-3">מטא-נתונים</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                  <div>
                    <p className="text-gray-600">תאריך יצירה</p>
                    <p className="font-medium">
                      {new Date(viewMapping.createdAt).toLocaleString('he-IL')}
                    </p>
                  </div>
                  <div>
                    <p className="text-gray-600">עדכון אחרון</p>
                    <p className="font-medium">
                      {new Date(viewMapping.updatedAt).toLocaleString('he-IL')}
                    </p>
                  </div>
                  {viewMapping.effectiveFrom && (
                    <div>
                      <p className="text-gray-600">תוקף מ-</p>
                      <p className="font-medium">
                        {new Date(viewMapping.effectiveFrom).toLocaleDateString('he-IL')}
                      </p>
                    </div>
                  )}
                  {viewMapping.effectiveUntil && (
                    <div>
                      <p className="text-gray-600">תוקף עד</p>
                      <p className="font-medium">
                        {new Date(viewMapping.effectiveUntil).toLocaleDateString('he-IL')}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="sticky bottom-0 bg-gray-50 border-t px-6 py-4 flex justify-between gap-2">
              <div>
                {isEditMode && (
                  <p className="text-sm text-gray-600">
                    שינויים ישמרו כגרסה חדשה (v{viewMapping.version + 1})
                  </p>
                )}
              </div>
              <div className="flex gap-2">
                {isEditMode ? (
                  <>
                    <button
                      onClick={handleCloseModal}
                      className="btn btn-outline"
                      disabled={saving}
                    >
                      ביטול
                    </button>
                    <button
                      onClick={handleSaveEdit}
                      className="btn btn-primary"
                      disabled={saving}
                    >
                      {saving ? 'שומר...' : 'שמור שינויים'}
                    </button>
                  </>
                ) : (
                  <>
                    <button onClick={handleCloseModal} className="btn btn-outline">
                      סגור
                    </button>
                    {viewMapping.status !== 'archived' && (
                      <button
                        onClick={() => setIsEditMode(true)}
                        className="btn btn-primary"
                      >
                        <Edit2 size={18} aria-hidden="true" />
                        עריכה
                      </button>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* System Modal */}
      <SystemModal
        isOpen={systemModal.isOpen}
        onClose={() => setSystemModal({ ...systemModal, isOpen: false })}
        type={systemModal.type}
        title={systemModal.title}
        message={systemModal.message}
      />
    </div>
  )
}
